import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FilePdf, Image as ImageIcon, UploadSimple, X, CheckCircle, Clock, Plus, ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { fmtDate } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import EmployeeEditDialog from "../components/EmployeeEditDialog";
import PairingsCard from "../components/PairingsCard";
import PersonNotesTrail from "../components/PersonNotesTrail";
import { DesignatedCaregiverCard } from "../components/DesignatedCaregiverCard";
import QuickNoteCard from "../components/QuickNoteCard";
import { FileGroups } from "../components/EmployeeFileGroups";
import BadgeCard from "../components/BadgeCard";
import { ReferenceCheckCard } from "../components/ReferenceCheckCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";


function OwnerBadge({ owner }) {
  return owner === "office" ? (
    <span className="rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary" data-testid="owner-office">waiting on you</span>
  ) : (
    <span className="rounded-full border border-[#B7791F]/30 bg-[#B7791F]/10 px-2 py-0.5 text-[10px] font-bold text-[#8a5d14]" data-testid="owner-them">waiting on them</span>
  );
}

export default function EmployeeFilePage() {
  const { uid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [emp, setEmp] = useState(null);
  const [file, setFile] = useState(null);
  const [viewDoc, setViewDoc] = useState(null);
  const [viewUrl, setViewUrl] = useState("");
  const [viewPages, setViewPages] = useState(0);
  const [docEdit, setDocEdit] = useState(null);
  const [uploadSection, setUploadSection] = useState("");
  const [taskToComplete, setTaskToComplete] = useState(null);
  const [sepToFinish, setSepToFinish] = useState(null);
  const [newTask, setNewTask] = useState({ title: "", owner: "them" });
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editSection, setEditSection] = useState("");
  const [busy, setBusy] = useState(false);
  const [trainings, setTrainings] = useState(null);
  const [visitSummary, setVisitSummary] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [navOrder, setNavOrder] = useState([]);
  const [headerBadge, setHeaderBadge] = useState(null);
  const fileInput = useRef(null);
  const taskFileInput = useRef(null);

  const load = () => {
    api.get(`/users/${uid}`).then((r) => setEmp(r.data.user || r.data)).catch(() => setEmp({ id: uid, name: "Employee" }));
    api.get(`/employees/${uid}/file`).then((r) => setFile(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get(`/users/${uid}/training-status`).then((r) => setTrainings(r.data.topics || [])).catch(() => setTrainings([]));
    api.get(`/employees/${uid}/visits`, { params: { page: 1 } }).then((r) => setVisitSummary(r.data.summary)).catch(() => setVisitSummary({ visits: 0 }));
    if (isAdmin) api.get(`/employees/${uid}/badges`).then((r) => setHeaderBadge((r.data.badges || [])[0] || null)).catch(() => {});
  };
  useEffect(() => { load(); }, [uid]);
  useEffect(() => {
    if (isAdmin) api.get("/users/nav-order").then((r) => setNavOrder(r.data.order || [])).catch(() => {});
  }, [isAdmin]);

  const navIdx = navOrder.findIndex((n) => n.id === uid || n.app_id === uid);
  const prevEmp = navIdx >= 0 ? navOrder[(navIdx - 1 + navOrder.length) % navOrder.length] : null;
  const nextEmp = navIdx >= 0 ? navOrder[(navIdx + 1) % navOrder.length] : null;

  const [bookBusy, setBookBusy] = useState(false);
  const downloadBook = async () => {
    setBookBusy(true);
    try {
      const r = await api.get(`/employees/${uid}/audit-book`, { responseType: "blob", timeout: 300000 });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `Silver_Home_Care_${(emp?.name || "employee").replace(/[^A-Za-z0-9]+/g, "_")}_Auditor_Book.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(formatApiError(e)); }
    setBookBusy(false);
  };

  const openDoc = async (d) => {
    setViewDoc(d);
    setViewUrl("");
    setViewPages(0);
    setDocEdit({
      title: d.title || "", doc_date: d.doc_date || "",
      issued: d.details?.issued || "", expires: d.details?.expires || "", result: d.details?.result || "",
      credential_no: d.details?.credential_no || "", issuer: d.details?.issuer || "",
    });
    try {
      if ((d.content_type || "").includes("pdf")) {
        // browsers won't reliably render blob-URL PDFs — render pages as images instead
        const r = await api.get(`/employee-file-docs/${d.id}/pages`);
        if (r.data.pages > 0) { setViewPages(r.data.pages); setViewUrl("pdf-pages"); return; }
      }
      const r = await api.get(`/employee-file-docs/${d.id}/file`, { responseType: "blob", timeout: 120000 });
      setViewUrl(URL.createObjectURL(new Blob([r.data], { type: d.content_type || "application/pdf" })));
    } catch (e) {
      toast.error(formatApiError(e));
      setViewDoc(null);
    }
  };

  const saveDocDetails = async () => {
    if (!viewDoc || !docEdit) return;
    setBusy(true);
    try {
      await api.patch(`/employee-file-docs/${viewDoc.id}`, {
        title: docEdit.title, doc_date: docEdit.doc_date,
        details: { issued: docEdit.issued, expires: docEdit.expires, result: docEdit.result, credential_no: docEdit.credential_no, issuer: docEdit.issuer },
      });
      setViewDoc({ ...viewDoc, title: docEdit.title || viewDoc.title, doc_date: docEdit.doc_date,
        details: { issued: docEdit.issued, expires: docEdit.expires, result: docEdit.result, credential_no: docEdit.credential_no, issuer: docEdit.issuer } });
      toast.success("Document details saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const uploadDoc = async (f) => {
    if (!f || !uploadSection) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/employees/${uid}/file-documents?section=${uploadSection}`, fd, { timeout: 300000 });
      toast.success("Added to the file");
      setUploadSection("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const completeTask = async (f) => {
    if (!taskToComplete) return;
    setBusy(true);
    try {
      const fd = new FormData();
      if (f) fd.append("file", f);
      await api.post(`/employees/${uid}/file-tasks/${taskToComplete.id}/complete`, fd, { timeout: 300000 });
      toast.success(`"${taskToComplete.title}" marked complete${f ? " — completed document filed" : ""}`);
      setTaskToComplete(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const addTask = async () => {
    setBusy(true);
    try {
      await api.post(`/employees/${uid}/file-tasks`, newTask);
      toast.success("To-complete item added");
      setAddOpen(false);
      setNewTask({ title: "", owner: "them" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  if (!emp || !file) {
    return <div className="flex justify-center py-24"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;
  }

  const openTasks = file.tasks.filter((t) => !t.done);
  const doneTasks = file.tasks.filter((t) => t.done);
  const archivedTasks = file.archived_tasks || [];
  const byId = {};
  Object.values(file.sections).flat().forEach((d) => { byId[d.id] = d; });
  const scores = emp.profile?.competency_scores || [];
  const findings = emp.profile?.audit_key_findings || "";
  const blanks = emp.profile?.audit_blanks || "";

  return (
    <div className="max-w-4xl space-y-6" data-testid="employee-file-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button onClick={() => navigate("/users")} className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-900" data-testid="back-to-roster">
          <ArrowLeft size={15} /> Back to roster
        </button>
        {navIdx >= 0 && (
          <div className="flex items-center gap-1 text-xs" data-testid="employee-nav">
            <button onClick={() => navigate(`/team/${prevEmp.id}`)} title={prevEmp.name}
              className="inline-flex max-w-44 items-center gap-1 rounded-full border border-stone-300 bg-white px-3 py-1 font-semibold text-stone-600 hover:bg-stone-50"
              data-testid="employee-nav-prev">
              <ArrowLeft size={12} /><span className="truncate">{prevEmp.name}</span>
            </button>
            <span className="px-1 text-stone-400" data-testid="employee-nav-position">{navIdx + 1} of {navOrder.length}</span>
            <button onClick={() => navigate(`/team/${nextEmp.id}`)} title={nextEmp.name}
              className="inline-flex max-w-44 items-center gap-1 rounded-full border border-stone-300 bg-white px-3 py-1 font-semibold text-stone-600 hover:bg-stone-50"
              data-testid="employee-nav-next">
              <span className="truncate">{nextEmp.name}</span><ArrowRight size={12} />
            </button>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="employee-file-header">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-start gap-3">
            {emp.documents?.badge_photo && (
              <img src={`${process.env.REACT_APP_BACKEND_URL}/api/users/${uid}/documents/badge_photo`} alt="Employee photo"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                className="h-24 w-24 rounded-xl border border-stone-200 object-cover shadow-sm" data-testid="header-profile-photo" />
            )}
            <div>
              <h1 className="font-heading text-3xl font-semibold tracking-tight text-stone-900" data-testid="employee-file-name">{emp.name}</h1>
              <p className="mt-1 text-sm text-stone-500">
                Employee #{emp.profile?.employee_id || "—"}
                {emp.profile?.rehire && " · rehire"}
              </p>
              <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-[13px]" data-testid="employee-file-dates">
                <span><span className="text-stone-400">Applied </span><b className="text-stone-700">{fmtDate(emp.profile?.application_date) || "—"}</b></span>
                <span><span className="text-stone-400">Hired </span><b className="text-stone-700">{fmtDate(emp.profile?.hire_date) || "—"}</b></span>
                <span><span className="text-stone-400">First worked (raw visits) </span><b className="text-stone-700" data-testid="header-first-worked">{fmtDate(emp.profile?.first_day_worked || emp.visit_first_worked) || "—"}</b></span>
                <span><span className="text-stone-400">Last worked (raw visits) </span><b className="text-stone-700" data-testid="header-last-worked">{fmtDate(emp.visit_last_worked) || "—"}</b></span>
                <span><span className="text-stone-400">Roster import </span><b className="text-stone-700" data-testid="header-roster-import">{fmtDate(emp.profile?.evv_utilization_as_of) || "never"}</b></span>
              </div>
            </div>
          </div>
          {headerBadge && (
            <div className="flex shrink-0 flex-col items-center gap-1 border-stone-200 sm:border-l sm:pl-8" data-testid="header-badge-block">
              <img src={`${process.env.REACT_APP_BACKEND_URL}/api/employees/${uid}/badges/${headerBadge.id}/file?fmt=png&inline=1`} alt="ID badge"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                className="h-28 rounded-lg border border-stone-200 shadow-sm" data-testid="header-badge-preview"
                title={`Badge — since ${headerBadge.year}`} />
              <span className="text-[10px] font-medium uppercase tracking-wide text-stone-400">ID badge</span>
            </div>
          )}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs" data-testid="employee-file-summary">
          <span className="rounded-full bg-stone-100 px-3 py-1 font-semibold text-stone-600">{file.summary.documents} documents</span>
          <span className="rounded-full bg-[#B7791F]/10 px-3 py-1 font-semibold text-[#8a5d14]">{file.summary.waiting_on_them} waiting on them</span>
          <span className="rounded-full bg-primary/10 px-3 py-1 font-semibold text-primary">{file.summary.waiting_on_office} waiting on you</span>
          {isAdmin && (
            <button onClick={() => { setEditSection(""); setEditOpen(true); }}
              className="rounded-full bg-stone-900 px-3 py-1 font-semibold text-white hover:bg-primary" data-testid="edit-record-btn">
              Edit record
            </button>
          )}
          {isAdmin && (
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/users/${uid}/employment-verification.pdf`}
              target="_blank" rel="noreferrer"
              className="rounded-full border border-stone-300 bg-white px-3 py-1 font-semibold text-stone-700 hover:bg-stone-50"
              data-testid="verification-letter-btn">
              Verification letter
            </a>
          )}
          {isAdmin && (
            <button onClick={downloadBook} disabled={bookBusy}
              className="rounded-full border border-stone-300 bg-white px-3 py-1 font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-50" data-testid="download-auditor-book">
              {bookBusy ? "Building…" : "Auditor book (PDF)"}
            </button>
          )}
        </div>
        {/* CONTACT & STATUS — merged into the main card (owner 2026-09-06) */}
        <div className="mt-5 border-t border-stone-100 pt-5" data-testid="header-contact-section">
          <FileGroups bare file={file} emp={emp} canUpload={isAdmin} onChanged={load} onlyKeys={["contact"]} showEmpty
            onUpload={(key) => { setUploadSection(key); setTimeout(() => fileInput.current?.click(), 50); }}
            onOpenDoc={openDoc} />
        </div>
        <PayrollCard embedded emp={emp} uid={uid} isAdmin={isAdmin} onChanged={load} />
      </div>

      {/* ID BADGE CREATOR — collapsed by default; sits with the badge (owner 2026-09-06) */}
      {isAdmin && (
        <BadgeCard uid={uid} emp={emp} isAdmin={isAdmin} onChanged={load} />
      )}

      {/* REFERENCES — right under the header block so they're never buried */}
      <ReferenceCheckCard uid={uid} emp={emp} docs={Object.values(file.sections || {}).flat()} isAdmin={isAdmin} onChanged={load} />

      {/* COMPLIANCE FLAGS — every rule, computed, clickable to fix */}
      {file.compliance && (
        <div className={`rounded-xl border p-5 shadow-sm ${file.compliance.blocks > 0 ? "border-[#C0392B]/30 bg-[#C0392B]/5" : file.compliance.warns > 0 ? "border-[#B7791F]/30 bg-[#B7791F]/5" : "border-[#27AE60]/30 bg-[#27AE60]/5"}`} data-testid="compliance-strip">
          <div className="flex flex-wrap items-center gap-2">
            <p className={`font-heading text-sm font-bold ${file.compliance.blocks > 0 ? "text-[#C0392B]" : file.compliance.warns > 0 ? "text-[#8a5d14]" : "text-[#1e8449]"}`} data-testid="compliance-summary">
              {file.compliance.summary}
            </p>
            <span className="rounded-full bg-white/70 px-2.5 py-0.5 text-[11px] font-semibold text-stone-600" data-testid="compliance-progress">
              {file.compliance.flags.filter((f) => f.status === "ok").length} of {file.compliance.flags.length} checks met
            </span>
            {file.compliance.provisional && (
              <span className="rounded-full bg-[#B7791F] px-2.5 py-0.5 text-[10px] font-bold text-white" data-testid="provisional-badge">PROVISIONAL HIRE</span>
            )}
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {file.compliance.flags.map((f) => {
              const editFor = { id_current: "id-docs", ss_card: "id-docs", ss_verify: "id-docs", i9: "id-docs", references: "references", pa_bg: "attestations", residency: "residency-fbi", fbi: "residency-fbi", drug_test: "drug-test", tb_screen: "tb", tb_test: "tb", orientation: "employment" }[f.key];
              if (f.status === "ok") return null;
              return (
                <button key={f.key} onClick={() => { if (isAdmin && editFor) { setEditSection(editFor); setEditOpen(true); } }}
                  className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${f.severity === "warn" ? "border-[#B7791F]/40 bg-white text-[#8a5d14]" : "border-[#C0392B]/40 bg-white text-[#C0392B]"} ${isAdmin ? "hover:shadow-sm cursor-pointer" : "cursor-default"}`}
                  title={f.detail} data-testid={`flag-${f.key}`}>
                  {f.label}{f.detail ? ` — ${f.detail}` : ""}
                </button>
              );
            })}
            {file.compliance.blocks === 0 && file.compliance.warns === 0 && (
              <p className="text-xs text-stone-500">ID, SS + verification, I-9 double-signed, references, PA BG reviewed, residency/FBI, drug test, TB, orientation — all clear.</p>
            )}
          </div>
        </div>
      )}

      {/* DOCUMENTS — every artifact, in Airtable order */}
      <p className="border-b border-stone-200 pb-2 font-heading text-xl font-semibold tracking-tight text-stone-900" data-testid="section-documents">
        Documents {isAdmin && <span className="ml-2 align-middle text-xs font-normal italic text-stone-400">drag a card to reorder within its section</span>}
      </p>
      <DesignatedCaregiverCard uid={uid} emp={emp} onSaved={load} />
      <FileGroups file={file} emp={emp} canUpload={isAdmin} onChanged={load} skipKeys={["contact"]}
        onUpload={(key) => { setUploadSection(key); setTimeout(() => fileInput.current?.click(), 50); }}
        onReorder={async (key, ids) => {
          try { await api.put(`/employees/${uid}/file-docs/reorder`, { order: ids }); load(); }
          catch (e) { toast.error(formatApiError(e)); }
        }}
        onToggleComplete={async (key, complete) => {
          try { await api.post(`/employees/${uid}/file-section-complete`, { section: key, complete }); load(); }
          catch (e) { toast.error(formatApiError(e)); }
        }}
        onOpenDoc={openDoc} />

      {/* AUDIT SUMMARY — flat facts from the audit pack */}
      {(scores.length > 0 || findings || emp.profile?.cpr_renew_by) && (
        <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="audit-summary-card">
          <h2 className="font-heading text-lg font-semibold text-stone-900">Audit summary — flat facts</h2>
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            {emp.profile?.hire_date && <span className="rounded-full bg-stone-100 px-3 py-1 font-semibold text-stone-600">Hire date {emp.profile.hire_date}</span>}
            {emp.profile?.cpr_renew_by && <span className="rounded-full bg-primary/10 px-3 py-1 font-semibold text-primary" data-testid="cpr-renew">CPR renew by {emp.profile.cpr_renew_by}</span>}
            {scores.map((s, i) => (
              <span key={i} className={`rounded-full px-3 py-1 font-semibold ${s.score >= 80 ? "bg-[#27AE60]/10 text-[#1e8449]" : "bg-[#C0392B]/10 text-[#C0392B]"}`} data-testid={`competency-score-${i}`}>
                Competency {s.date}: {s.score}%
              </span>
            ))}
          </div>
          {findings && (
            <pre className="mt-4 max-h-72 overflow-y-auto whitespace-pre-wrap rounded-lg bg-accent/50 p-4 font-sans text-xs leading-relaxed text-stone-600" data-testid="audit-findings">{findings}</pre>
          )}
          {blanks && (
            <p className="mt-3 rounded-lg border border-[#B7791F]/25 bg-[#B7791F]/5 px-4 py-2 text-xs text-stone-600" data-testid="audit-blanks">
              <b>Not on file per the audit:</b> {blanks}
            </p>
          )}
        </div>
      )}



      {/* ONBOARDING & ORIENTATION */}
      <p className="border-b border-stone-200 pb-2 font-heading text-xl font-semibold tracking-tight text-stone-900" data-testid="section-onboarding">Onboarding &amp; orientation</p>
      {/* TO COMPLETE — the who-waits-on-whom board */}
      <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="employee-file-tasks">
        <div className="flex items-center justify-between gap-2">
          <div>
            <h2 className="font-heading text-lg font-semibold text-stone-900">To complete</h2>
            <p className="text-xs text-stone-500">Every outstanding item, with its owner. Upload the completed document to close an item — it files itself.</p>
          </div>
          {isAdmin && (
            <Button size="sm" variant="outline" onClick={() => setAddOpen(true)} className="rounded-full border-stone-300 text-xs" data-testid="add-task-btn">
              <Plus size={13} className="mr-1" /> Add item
            </Button>
          )}
        </div>
        {openTasks.length === 0 ? (
          <p className="mt-4 rounded-lg border border-dashed border-stone-200 px-4 py-6 text-center text-sm text-[#1e8449]" data-testid="tasks-all-done">File complete — nothing outstanding.</p>
        ) : (
          <div className="mt-4 space-y-2">
            {openTasks.map((t) => {
              const blank = t.blank_doc_id ? byId[t.blank_doc_id] : null;
              return (
                <div key={t.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-stone-200 px-4 py-3" data-testid={`task-${t.id}`}>
                  <Clock size={16} className="shrink-0 text-stone-300" />
                  <div className="min-w-40 flex-1">
                    <p className="text-sm font-medium text-stone-900">{t.title}</p>
                    {blank && (
                      <button onClick={() => openDoc(blank)} className="mt-0.5 text-xs font-medium text-primary hover:underline" data-testid={`task-blank-${t.id}`}>
                        View the blank form →
                      </button>
                    )}
                  </div>
                  <OwnerBadge owner={t.owner} />
                  {isAdmin && (
                    <Button size="sm" onClick={() => setTaskToComplete(t)} className="rounded-full bg-primary text-xs text-primary-foreground hover:bg-stone-900" data-testid={`task-complete-${t.id}`}>
                      Mark complete
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {doneTasks.length > 0 && (
          <div className="mt-4 space-y-1.5 border-t border-stone-100 pt-3">
            {doneTasks.map((t) => (
              <p key={t.id} className="flex items-center gap-2 text-xs text-stone-400" data-testid={`task-done-${t.id}`}>
                <CheckCircle size={14} className="text-[#27AE60]" /> {t.title} — done {(t.done_at || "").slice(0, 10)}
                {t.done_doc_id && byId[t.done_doc_id] && (
                  <button onClick={() => openDoc(byId[t.done_doc_id])} className="font-medium text-primary hover:underline">view</button>
                )}
              </p>
            ))}
          </div>
        )}
        {archivedTasks.length > 0 && (
          <div className="mt-4 border-t border-stone-100 pt-3">
            <button onClick={() => setShowArchived((v) => !v)}
              className="text-xs font-medium text-stone-400 hover:text-stone-600" data-testid="archived-tasks-toggle">
              {showArchived ? "Hide" : "Show"} {archivedTasks.length} archived audit note{archivedTasks.length === 1 ? "" : "s"} — historical items from the imported auditor book
            </button>
            {showArchived && (
              <div className="mt-2 space-y-1.5" data-testid="archived-tasks-list">
                {archivedTasks.map((t) => (
                  <p key={t.id} className="flex items-center gap-2 text-xs italic text-stone-400" data-testid={`task-archived-${t.id}`}>
                    <Clock size={13} className="shrink-0" /> {t.title}
                  </p>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <PairingsCard uid={uid} />

      {/* ONGOING TRAINING */}
      <p className="border-b border-stone-200 pb-2 font-heading text-xl font-semibold tracking-tight text-stone-900" data-testid="section-training">Ongoing training</p>
      <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="employee-trainings">
        {trainings === null ? (
          <p className="text-xs text-stone-400">Loading…</p>
        ) : trainings.length === 0 ? (
          <p className="text-xs italic text-stone-400">No active training topics.</p>
        ) : (
          <div className="divide-y divide-stone-100">
            {trainings.map((t) => (
              <div key={t.topic_id} className="flex flex-wrap items-center justify-between gap-2 py-2.5" data-testid={`training-row-${t.topic_id}`}>
                <p className="text-sm font-medium text-stone-800">{t.title}</p>
                <span className="flex items-center gap-3 text-xs">
                  {t.last_done && <span className="text-stone-400">Last done {t.last_done}</span>}
                  {t.renew_by && <span className="text-stone-400">Renew by {t.renew_by}</span>}
                  <span className={`rounded-full border px-2.5 py-0.5 font-semibold ${
                    t.status === "overdue" || t.status === "never" ? "border-[#C0392B]/40 text-[#C0392B]"
                    : t.status === "due_soon" ? "border-[#B7791F]/40 text-[#8a5d14]"
                    : "border-[#27AE60]/40 text-[#1e8449]"}`} data-testid={`training-status-${t.topic_id}`}>
                    {t.status === "never" ? "never done" : t.status.replace("_", " ")}
                  </span>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* VISIT HISTORY */}
      <p className="border-b border-stone-200 pb-2 font-heading text-xl font-semibold tracking-tight text-stone-900" data-testid="section-visits">Visit history</p>
      <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="employee-visits-card">
        {visitSummary === null ? (
          <p className="text-xs text-stone-400">Loading…</p>
        ) : !visitSummary.visits ? (
          <p className="text-xs italic text-stone-400" data-testid="visits-card-empty">No visits on record for this employee.</p>
        ) : (
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <span className="text-sm font-semibold text-stone-900" data-testid="visits-card-count">{visitSummary.visits.toLocaleString()} visits</span>
            <span className="text-sm text-stone-600">{visitSummary.hours.toLocaleString()} hrs</span>
            <span className="text-sm text-stone-600">{visitSummary.clients} client{visitSummary.clients === 1 ? "" : "s"}</span>
            {visitSummary.missed > 0 && <span className="text-sm font-medium text-[#C0392B]">{visitSummary.missed} missed</span>}
            <span className="text-xs text-stone-400">{visitSummary.first} → {visitSummary.last}</span>
            <Link to={`/team/${uid}/visits`} className="ml-auto text-sm font-medium text-primary hover:underline" data-testid="open-visit-history">
              Open full visit history →
            </Link>
          </div>
        )}
      </div>

      {/* PAYROLL — (rendered up top under the header since 2026-09-05) */}

      {/* EMPLOYMENT RECORD — status changes & separation forms */}
      {(emp.employment_records || []).some((r) => r.kind === "separation" || r.kind === "status-change") && (
        <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="employment-record-block">
          <p className="border-b border-stone-200 pb-2 font-heading text-xl tracking-tight text-stone-900">Employment record</p>
          <ul className="mt-3 space-y-2">
            {[...(emp.employment_records || [])].filter((r) => r.kind === "separation" || r.kind === "status-change").reverse().map((r) => {
              const incomplete = r.kind === "separation" && !(r.notes && r.character_notes);
              return (
                <li key={r.id} className="rounded-lg border border-stone-100 px-3 py-2" data-testid={`emp-record-${r.id}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${r.kind === "separation" ? "border-[#C0392B]/30 bg-[#C0392B]/5 text-[#C0392B]" : "border-stone-300 bg-stone-50 text-stone-600"}`}>
                      {r.kind === "separation" ? (r.status === "resigned" ? "Resignation" : "Termination") : `Status → ${r.status}`}
                    </span>
                    <span className="text-xs text-stone-500">effective {r.effective_date}{r.reason ? ` · ${r.reason}` : ""} · by {r.by_name || r.by}</span>
                    {incomplete && (
                      <button onClick={() => setSepToFinish(r)}
                        className="ml-auto rounded-full bg-[#B7791F] px-3 py-1 text-[11px] font-semibold text-white hover:bg-[#8a5d14]"
                        data-testid={`finish-separation-${r.id}`}>Finish this form</button>
                    )}
                    {r.kind === "separation" && (
                      <>
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/users/${uid}/separation/${r.id}/letter`} target="_blank" rel="noreferrer"
                          className="text-[11px] font-semibold text-[#4A235A] hover:underline" data-testid={`separation-letter-${r.id}`}>Letter (PDF)</a>
                        <button onClick={async () => {
                          try {
                            await api.post("/admin/tasks/mark-done", { ref_id: r.id, kind: "termination_letter" });
                            toast.success("Marked — letter sent. The daily reminder stops.");
                          } catch (e) { toast.error(formatApiError(e)); }
                        }}
                          className="text-[11px] font-semibold text-stone-500 hover:text-stone-800" data-testid={`separation-letter-sent-${r.id}`}>Mark letter sent</button>
                      </>
                    )}
                  </div>
                  {r.notes && <p className="mt-1 text-xs text-stone-600">{r.notes}</p>}
                  {r.character_notes && <p className="mt-0.5 text-xs italic text-stone-500">Character: {r.character_notes}</p>}
                  {r.kind === "separation" && (
                    <p className="mt-0.5 text-[11px] text-stone-400">{r.rehire_eligible ? "Eligible for rehire" : "Not marked rehire-eligible"}</p>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {/* NOTES */}
      <p className="border-b border-stone-200 pb-2 font-heading text-xl font-semibold tracking-tight text-stone-900" data-testid="section-notes">Notes</p>
      <PersonNotesTrail testid="employee-notes-trail"
        notes={(emp.notes || []).map((n) => ({ id: n.id, text: n.text, type: n.type, date: n.at, by: n.by, tags: n.tags || [] }))} />

      <QuickNoteCard presetEmployee={{ id: uid, name: emp.name }} compact />

      {/* AIRTABLE GROUPS — record fields + flags + every artifact visible as a thumbnail */}
      <EmployeeEditDialog emp={emp} open={editOpen} section={editSection} onClose={() => setEditOpen(false)} onSaved={load} />

      {/* hidden inputs */}
      <input ref={fileInput} type="file" className="hidden" onChange={(e) => { uploadDoc(e.target.files?.[0]); e.target.value = ""; }} data-testid="section-file-input" />

      {/* complete-task dialog */}
      <Dialog open={!!taskToComplete} onOpenChange={(o) => !o && setTaskToComplete(null)}>
        <DialogContent data-testid="complete-task-dialog">
          <DialogHeader><DialogTitle>Mark complete: {taskToComplete?.title}</DialogTitle></DialogHeader>
          <p className="text-sm text-stone-500">Attach the completed/signed document and it files itself into the record — or mark complete with no document.</p>
          <div className="flex flex-wrap gap-2 pt-2">
            <Button onClick={() => taskFileInput.current?.click()} disabled={busy} className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="complete-with-upload">
              <UploadSimple size={14} className="mr-1.5" /> Upload completed document
            </Button>
            <input ref={taskFileInput} type="file" className="hidden" onChange={(e) => { completeTask(e.target.files?.[0]); e.target.value = ""; }} data-testid="complete-file-input" />
            <Button variant="outline" onClick={() => completeTask(null)} disabled={busy} className="rounded-full border-stone-300" data-testid="complete-no-doc">
              Just mark complete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* add-task dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-task-dialog">
          <DialogHeader><DialogTitle>Add a to-complete item</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={newTask.title} onChange={(e) => setNewTask((v) => ({ ...v, title: e.target.value }))}
              placeholder="e.g. Signed job description acknowledgment" className="border-stone-300" data-testid="add-task-title" />
            <div className="flex gap-2" data-testid="add-task-owner">
              {[["them", "They owe it"], ["office", "Office owes it"]].map(([v, l]) => (
                <button key={v} onClick={() => setNewTask((p) => ({ ...p, owner: v }))}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${newTask.owner === v ? "border-primary bg-primary text-primary-foreground" : "border-stone-300 text-stone-500"}`}
                  data-testid={`add-task-owner-${v}`}>{l}</button>
              ))}
            </div>
            <Button onClick={addTask} disabled={busy || !newTask.title.trim()} className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="add-task-submit">Add item</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* artifact viewer */}
      <Dialog open={!!viewDoc} onOpenChange={(o) => { if (!o) { setViewDoc(null); setViewUrl(""); setViewPages(0); } }}>
        <DialogContent className="flex max-h-[92vh] max-w-6xl flex-col overflow-hidden" data-testid="doc-viewer-dialog">
          <DialogHeader><DialogTitle className="text-sm">{viewDoc?.title}{viewDoc?.doc_date ? ` · ${viewDoc.doc_date}` : ""}</DialogTitle></DialogHeader>
          <div className="flex min-h-0 flex-1 flex-col gap-3 sm:flex-row">
          {docEdit && (
            <div className="w-full shrink-0 space-y-2 overflow-y-auto rounded-lg border border-stone-200 bg-stone-50 p-3 sm:max-h-[76vh] sm:w-72" data-testid="doc-details-editor">
              <label className="block text-[11px] text-stone-500">Document date
                <input type="date" value={docEdit.doc_date} onChange={(e) => setDocEdit({ ...docEdit, doc_date: e.target.value })}
                  className="mt-0.5 w-full rounded-md border border-stone-300 bg-white px-2 py-1 text-xs" data-testid="doc-edit-date" />
              </label>
              <label className="block text-[11px] text-stone-500">Issued (date)
                <input type="date" value={docEdit.issued} onChange={(e) => setDocEdit({ ...docEdit, issued: e.target.value })}
                  className="mt-0.5 w-full rounded-md border border-stone-300 bg-white px-2 py-1 text-xs" data-testid="doc-edit-issued" />
              </label>
              <label className="block text-[11px] text-stone-500">Expires / valid through
                <input type="date" value={docEdit.expires} onChange={(e) => setDocEdit({ ...docEdit, expires: e.target.value })}
                  className="mt-0.5 w-full rounded-md border border-stone-300 bg-white px-2 py-1 text-xs" data-testid="doc-edit-expires" />
              </label>
              <label className="block text-[11px] text-stone-500">Result
                <input value={docEdit.result} onChange={(e) => setDocEdit({ ...docEdit, result: e.target.value })} placeholder="Negative, clear, pass…"
                  className="mt-0.5 w-full rounded-md border border-stone-300 bg-white px-2 py-1 text-xs" data-testid="doc-edit-result" />
              </label>
              <label className="block text-[11px] text-stone-500">Credential / reference no.
                <input value={docEdit.credential_no} onChange={(e) => setDocEdit({ ...docEdit, credential_no: e.target.value })}
                  className="mt-0.5 w-full rounded-md border border-stone-300 bg-white px-2 py-1 text-xs" data-testid="doc-edit-credential-no" />
              </label>
              <label className="block text-[11px] text-stone-500">Issued by
                <input value={docEdit.issuer} onChange={(e) => setDocEdit({ ...docEdit, issuer: e.target.value })} placeholder="e.g. PA State Police"
                  className="mt-0.5 w-full rounded-md border border-stone-300 bg-white px-2 py-1 text-xs" data-testid="doc-edit-issuer" />
              </label>
              <label className="block text-[11px] text-stone-500">Title
                <input value={docEdit.title} onChange={(e) => setDocEdit({ ...docEdit, title: e.target.value })}
                  className="mt-0.5 w-full rounded-md border border-stone-300 bg-white px-2 py-1 text-xs" data-testid="doc-edit-title" />
              </label>
              <div className="pt-1">
                <Button size="sm" onClick={saveDocDetails} disabled={busy}
                  className="w-full rounded-full bg-primary px-3 text-xs text-primary-foreground" data-testid="doc-edit-save">
                  {busy ? "Saving…" : "Save details"}
                </Button>
                <p className="mt-1 text-[10px]">
                  {viewDoc?.doc_date_source === "ocr"
                    ? <span className="font-medium text-[#8a5d14]">Date auto-read by OCR — verify it against the document before relying on it.</span>
                    : viewDoc?.doc_date_source === "import"
                      ? <span className="text-stone-400">Date came from the imported file index.</span>
                      : <span className="text-stone-400">Anything you type here is marked hand-entered.</span>}
                </p>
              </div>
            </div>
          )}
          <div className="min-h-0 min-w-0 flex-1">
          {!viewUrl ? (
            <div className="flex h-96 items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>
          ) : viewPages > 0 ? (
            <div className="max-h-[76vh] space-y-3 overflow-y-auto pr-1" data-testid="doc-viewer-pages">
              {Array.from({ length: viewPages }, (_, i) => (
                <img key={i} src={`${api.defaults.baseURL}/employee-file-docs/${viewDoc.id}/page/${i + 1}`}
                  alt={`${viewDoc?.title} page ${i + 1}`} loading="lazy"
                  className="mx-auto w-full rounded-lg border border-stone-200" data-testid={`doc-viewer-page-${i + 1}`} />
              ))}
            </div>
          ) : (viewDoc?.content_type || "").startsWith("image/") ? (
            <img src={viewUrl} alt={viewDoc?.title} className="mx-auto max-h-[76vh] rounded-lg border border-stone-200 object-contain" data-testid="doc-viewer-image" />
          ) : (viewDoc?.content_type || "").includes("pdf") ? (
            <iframe src={viewUrl} title={viewDoc?.title} className="h-[76vh] w-full rounded-lg border border-stone-200 bg-white" data-testid="doc-viewer-frame" />
          ) : (
            <div className="py-10 text-center" data-testid="doc-viewer-download">
              <p className="text-sm text-stone-500">This file type doesn't preview in the browser.</p>
              <a href={viewUrl} download={viewDoc?.filename} className="mt-3 inline-block rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">Download</a>
            </div>
          )}
          </div>
          </div>
        </DialogContent>
      </Dialog>
      {sepToFinish && (
        <SeparationFinishModal uid={uid} rec={sepToFinish}
          onClose={() => setSepToFinish(null)}
          onSaved={() => { setSepToFinish(null); load(); }} />
      )}
    </div>
  );
}

function PayrollCard({ emp, uid, isAdmin, onChanged, embedded = false }) {
  const p = emp.profile || {};
  const payRate = emp.pay_rate ?? p.pay_rate;
  const [open, setOpen] = useState(false);
  const [rate, setRate] = useState(payRate ?? "");
  const [odp, setOdp] = useState(!!p.on_demand_pay);
  const [evv, setEvv] = useState(!!p.evv_bonus);
  const [salary, setSalary] = useState(!!p.salary);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/users/${uid}/payroll`, { pay_rate: rate === "" ? null : rate, on_demand_pay: odp, evv_bonus: evv, salary });
      toast.success("Payroll details saved");
      setOpen(false);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className={embedded ? "mt-3 border-t border-stone-100 pt-3" : "rounded-xl border border-stone-200 bg-white p-5 shadow-sm"} data-testid="payroll-card">
      <div className="flex items-center gap-2">
        <p className={embedded ? "text-[10px] font-semibold uppercase tracking-wider text-stone-400" : "font-heading text-xl tracking-tight text-stone-900"}>Payroll</p>
        {isAdmin && (
          <button onClick={() => setOpen((v) => !v)} className={`${embedded ? "" : "ml-auto "}text-xs font-medium text-stone-500 hover:text-primary`} data-testid="payroll-edit-btn">
            {open ? "Close" : "Edit"}
          </button>
        )}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
        <span className="font-semibold text-stone-800" data-testid="payroll-rate">
          {p.salary ? "Salaried" : payRate ? `$${Number(payRate).toFixed(2)}/hr` : "rate not set"}
        </span>
        {p.on_demand_pay && <span className="rounded-full border border-[#B7791F]/30 bg-[#B7791F]/10 px-2 py-0.5 text-[11px] font-semibold text-[#8a5d14]" data-testid="payroll-odp">on-demand pay</span>}
        {p.evv_bonus && <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700" data-testid="payroll-evv">EVV bonus</span>}
        {p.payroll_imported && <span className="text-[10px] text-stone-400">from {p.payroll_imported}</span>}
      </div>
      {open && isAdmin && (
        <div className="mt-3 flex flex-wrap items-end gap-3 rounded-lg border border-dashed border-stone-300 p-3" data-testid="payroll-edit-form">
          <label className="text-xs text-stone-600">
            Pay rate ($/hr)
            <input type="number" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)}
              className="mt-1 block w-28 rounded-md border border-stone-300 px-2 py-1.5 text-sm" data-testid="payroll-rate-input" />
          </label>
          <label className="flex items-center gap-1.5 text-xs text-stone-700">
            <input type="checkbox" checked={odp} onChange={(e) => setOdp(e.target.checked)} className="accent-[#4A235A]" data-testid="payroll-odp-input" /> On-demand pay
          </label>
          <label className="flex items-center gap-1.5 text-xs text-stone-700">
            <input type="checkbox" checked={evv} onChange={(e) => setEvv(e.target.checked)} className="accent-[#4A235A]" data-testid="payroll-evv-input" /> EVV bonus
          </label>
          <label className="flex items-center gap-1.5 text-xs text-stone-700">
            <input type="checkbox" checked={salary} onChange={(e) => setSalary(e.target.checked)} className="accent-[#4A235A]" data-testid="payroll-salary-input" /> Salaried
          </label>
          <button onClick={save} disabled={busy}
            className="rounded-full bg-[#4A235A] px-4 py-1.5 text-xs font-semibold text-white hover:bg-stone-900 disabled:opacity-50"
            data-testid="payroll-save">{busy ? "Saving…" : "Save"}</button>
        </div>
      )}
    </div>
  );
}

function SeparationFinishModal({ uid, rec, onClose, onSaved }) {  const [reason, setReason] = useState(rec.reason || "");
  const [notes, setNotes] = useState(rec.notes || "");
  const [character, setCharacter] = useState(rec.character_notes || "");
  const [rehire, setRehire] = useState(!!rec.rehire_eligible);
  const [busy, setBusy] = useState(false);
  const REASONS = ["Attendance / call-outs", "Performance", "Policy violation", "Compliance lapse", "Job abandonment / no contact", "New job", "Personal / family", "Schedule", "Pay", "Moved away", "No call, no show", "Other"];

  const save = async () => {
    if (!notes.trim() || !character.trim()) {
      toast.error("The story and the character notes are what close this out — write both.");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/users/${uid}/separation/${rec.id}`, { reason, notes, character_notes: character, rehire_eligible: rehire });
      toast.success("Separation form completed — the daily reminder stops.");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="separation-finish-modal">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <div className="flex items-start justify-between">
          <div>
            <p className="font-heading text-xl tracking-tight text-[#4A235A]">Finish the {rec.status} form</p>
            <p className="mt-0.5 text-xs text-stone-500">Effective {rec.effective_date} · filed {String(rec.at || "").slice(0, 10)}</p>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700" data-testid="sep-finish-close"><X size={18} /></button>
        </div>
        <div className="mt-4 space-y-3 text-left">
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-stone-400">Reason</span>
            <select value={reason} onChange={(e) => setReason(e.target.value)}
              className="mt-1 w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm" data-testid="sep-finish-reason">
              <option value="">Choose…</option>
              {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-stone-400">What happened *</span>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
              className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 text-sm" data-testid="sep-finish-notes" />
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-stone-400">What did this reveal about them? *</span>
            <textarea value={character} onChange={(e) => setCharacter(e.target.value)} rows={2}
              placeholder="How they handled it — accountability, blame, honesty, silence…"
              className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 text-sm" data-testid="sep-finish-character" />
          </label>
          <label className="flex items-center gap-2 text-sm text-stone-700">
            <input type="checkbox" checked={rehire} onChange={(e) => setRehire(e.target.checked)} className="accent-[#4A235A]" data-testid="sep-finish-rehire" />
            Eligible for rehire
          </label>
        </div>
        <div className="mt-4 flex gap-2">
          <button onClick={save} disabled={busy}
            className="flex-1 rounded-full bg-[#4A235A] px-5 py-2 text-sm font-semibold text-white hover:bg-stone-900 disabled:opacity-50"
            data-testid="sep-finish-save">{busy ? "Saving…" : "Complete the form"}</button>
          <button onClick={onClose} className="rounded-full border border-stone-300 px-5 py-2 text-sm text-stone-600 hover:bg-stone-50" data-testid="sep-finish-cancel">Later</button>
        </div>
      </div>
    </div>
  );
}
